/**
 * Costs Service
 *
 * API endpoints for investment cost operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentCost, InvestmentCostWithCategory } from '@medsuite/models';

export interface CreateCostDto {
  investment_id: number;
  cost_category_id: number;
  cost_name: string;
  cost_details?: string;
  cost_assumption_details?: string;
  is_perpetual: boolean;
  period?: Date | string;
  capex_cost: number;
  opex_cost: number;
}

export interface UpdateCostDto extends Partial<Omit<CreateCostDto, 'investment_id'>> {}

export interface CostSummary {
  total_capex: number;
  total_opex: number;
  grand_total: number;
  by_category: {
    cost_category_id: number;
    cost_category_name: string;
    capex: number;
    opex: number;
    total: number;
  }[];
}

/**
 * Costs Service
 */
class CostsService {
  /**
   * Get all costs for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<InvestmentCostWithCategory[]>> {
    return apiClient.get<InvestmentCostWithCategory[]>(`/investments/${investmentId}/costs`);
  }

  /**
   * Get single cost by ID
   */
  async getById(investmentId: number, costId: number): Promise<ApiResponse<InvestmentCost>> {
    return apiClient.get<InvestmentCost>(`/investments/${investmentId}/costs/${costId}`);
  }

  /**
   * Get cost summary with totals
   */
  async getSummary(investmentId: number): Promise<ApiResponse<CostSummary>> {
    return apiClient.get<CostSummary>(`/investments/${investmentId}/costs/summary`);
  }

  /**
   * Create new cost
   */
  async create(
    investmentId: number,
    data: Omit<CreateCostDto, 'investment_id'>
  ): Promise<ApiResponse<InvestmentCost>> {
    return apiClient.post<InvestmentCost>(`/investments/${investmentId}/costs`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update cost
   */
  async update(
    investmentId: number,
    costId: number,
    data: UpdateCostDto
  ): Promise<ApiResponse<InvestmentCost>> {
    return apiClient.put<InvestmentCost>(`/investments/${investmentId}/costs/${costId}`, data);
  }

  /**
   * Delete cost
   */
  async delete(investmentId: number, costId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/costs/${costId}`);
  }
}

export const costsService = new CostsService();
